import * as React from 'react'
import * as moment from 'moment'
import { RouteComponentProps, withRouter } from 'react-router'
import { withTranslation, WithTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons/faCircleNotch'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle'
import { faEyeDropper } from '@fortawesome/free-solid-svg-icons/faEyeDropper'
import { faHourglassHalf } from '@fortawesome/free-solid-svg-icons/faHourglassHalf'
import { Workshift } from '@mv-submodules/inplant-plantanalysis-fe-iblu/types/workshift'
import { GeneralData } from '@mv-submodules/inplant-plantanalysis-fe-iblu/types/measure'
import { generalFetchData } from '@mv-submodules/inplant-plantanalysis-fe-iblu/redux/actions/general'
import {
  resetWorkshiftFilter,
  updateDateFilter,
  updateWorkshiftFilter,
} from '@mv-submodules/inplant-plantanalysis-fe-iblu/redux/actions/common'
import { modelFetchData } from '@mv-submodules/inplant-plantanalysis-fe-iblu/redux/actions/model'
import { flatData } from '@mv-submodules/inplant-plantanalysis-fe-iblu/functions/model'
import WorkshiftSelector from '@mv-submodules/inplant-plantanalysis-fe-iblu/ui/components/widgets/WorkShiftSelector/WorkshiftSelector'
import GraphWrapper from '@mv-submodules/inplant-plantanalysis-fe-iblu/ui/components/widgets/GraphWrapper/GraphWrapperView'
import GeneralGraphs from '@mv-submodules/inplant-plantanalysis-fe-iblu/ui/components/widgets/GeneralGraphs/GeneralGraphsView'
import { ModelNodeData } from '@mv-submodules/inplant-plantanalysis-fe-iblu/types/chronograph'
import FanGraphs from '@mv-submodules/inplant-plantanalysis-fe-iblu/ui/components/widgets/FanGraph/FanGraphs'
import BagGraphs from '@mv-submodules/inplant-plantanalysis-fe-iblu/ui/components/widgets/BagGraph/BagGraphs'
import MultiSeriesLineCharts from '@mv-submodules/inplant-plantanalysis-fe-iblu/ui/components/widgets/MultiSeriesLineCharts/MultiSeriesLineCharts'
import Row from '@mv-submodules/inplant-components-fe/ui/components/Grid/Row'
import Column from '@mv-submodules/inplant-components-fe/ui/components/Grid/Column'
import BeltBinarySrcGraphs, {
  materialTemperatureAdditionalLine,
  waterFlowAdditionalLine,
} from '@mv-submodules/inplant-plantanalysis-fe-iblu/ui/components/widgets/BeltGraph/BeltBinarySrcGraphs'
import BeltBinarySrcGenGraphs from '@mv-submodules/inplant-plantanalysis-fe-iblu/ui/components/widgets/BeltGraph/BeltBinarySrcGenGraphs'
import { PlantAnalysisDetailsConfigurationExtended } from '../../../../types/settings'
import PressDetail from '../../widgets/Press/PressDetail'
import AspirationSystem from '../../widgets/AspirationSystem/AspirationSystem'
import GenericBinarySrcGraph from '../../widgets/GeneralBinarySrcGraph/GenericBinarySrcGraph'
import { ChartMeasure, ChartPlugin, ChartWordMeasure } from '../../widgets/GeneralBinarySrcGraph/_types'
import { BuffersFillGraph } from '../../widgets/Buffers'
import { isWordSlug, WordSlug } from '@mv-submodules/inplant-plantanalysis-fe-iblu/functions/influx/measures-utils'
import speedAbsorptionRatioSummaryPlugin from '../../widgets/GeneralBinarySrcGraph/plugins/speedAbsorptionRatioSummary/speedAbsorptionRatioSummary'
import BeltChart from '../../widgets/DetailCharts/BeltChart/BeltChart'
import BunkerChart from '../../widgets/DetailCharts/BunkerChart/BunkerChart'
import ChartWrapper from '../../widgets/DetailCharts/Common/ChartWrapper'

library.add(faCircleNotch, faInfoCircle, faEyeDropper, faHourglassHalf)

let ticking = false

const loadingBeltIdToDensifierId = {
  IBSGN5PHK0M8OLFX: 'IBSGN5PHK0N0DCJP',
  IBSGN5PHK0NLEGEO: 'IBSGN5PHK0OBEPNL',
  IBSGN5PK9VUBKBVR: 'IBSGN5PKA065B3AF',
  IBSGN5PQBEBLKOFK: 'IBSGN5PP2MAITHBU',
  IBSGN5PQBEHP88TX: 'IBSGN5PP2MBA6KED',
}
/**
 * These assets have no data to display. There is no common way of hiding these specific assets so the list is hardcoded here.
 * TODO: find a better logic for hiding these assets
 */
const ASSETS_CODES_TO_BE_IGNORED = ['F302', 'F305', 'F311', 'F312', 'F313', 'F334', 'F335', 'F340', 'F341', 'F349']

interface OwnState {
  startDate?: moment.Moment
  endDate?: moment.Moment
  currentGraph: string[]
  lastScrollY: number
}

interface StateProps {
  detailsConfiguration: PlantAnalysisDetailsConfigurationExtended | null
  dateFilterStart: moment.Moment
  workshift: Workshift | null
  lavorazioneAttuale: null | GeneralData
  ricettaAttuale: null | GeneralData
  plant: any | null
  model: null | GeneralData
  chartConfigurations: any | null
  workshifts: null | GeneralData
}

const mapStateToProps = (state: any): StateProps => ({
  detailsConfiguration: {
    'ric-sg': {
      MDIR: {
        binaryMapping: {
          manuale: 1,
          automatico: 2,
          locale: 4,
          sezionato: 8,
          running: 64,
          avanti: 0,
          bypass: 0,
          indietro: 0,
          inverter: 0,
          marcia: 0,
        },
      },
      INV: {
        binaryMapping: {
          manuale: 1,
          automatico: 2,
          locale: 4,
          sezionato: 8,
          avanti: 64,
          indietro: 128,
          bypass: 0,
          inverter: 0,
          marcia: 0,
          running: 0,
        },
      },
      DIR: {
        binaryMapping: {
          manuale: 1,
          automatico: 2,
          locale: 4,
          sezionato: 8,
          avanti: 64,
          indietro: 128,
          bypass: 0,
          inverter: 0,
          marcia: 0,
          running: 0,
        },
      },
      default: {
        binaryMapping: {
          manuale: 1,
          automatico: 2,
          locale: 4,
          sezionato: 8,
          avanti: 64,
          indietro: 128,
          bypass: 0,
          inverter: 0,
          marcia: 0,
          running: 64,
        },
      },
    },
  },
  /* state.config?.plantAnalysis?.detailsConfiguration && isJSON(state.config.plantAnalysis.detailsConfiguration)
      ? JSON.parse(state.config.plantAnalysis.detailsConfiguration)
      : undefined */ dateFilterStart:
    state.plantAnalysis.common.dateFilterStart,
  workshift: state.plantAnalysis.common.workshift,
  lavorazioneAttuale: state.plantAnalysis.general.lavorazione,
  ricettaAttuale: state.plantAnalysis.general.ricetta,
  plant: state.plantSelector || null,
  model: state.plantAnalysis.model,
  chartConfigurations: state.config && state.config.plantAnalysis && state.config.plantAnalysis.chartConfigurations,
  workshifts: state.plantAnalysis.workshifts.workshifts,
})

interface DispatchProps {
  generalFetchData: (workshift: Workshift) => void
  resetWorkshift: () => void
  modelFetchData: (plant?: string) => void
  updateWorkshiftFilter: (workshift: number, workshifts: GeneralData | null) => void
  updateDateFilter: (dateStart: moment.Moment, dateEnd: moment.Moment, isManual?: boolean) => void
}

const mapDispatchToProps = (dispatch: Function): DispatchProps => {
  return {
    generalFetchData: (workshift: Workshift) => dispatch(generalFetchData(workshift)),
    resetWorkshift: (workshift?: Workshift) => dispatch(resetWorkshiftFilter(workshift)),
    modelFetchData: (plant?: string) => dispatch(modelFetchData(plant)),
    updateWorkshiftFilter: (workshift: number, workshifts: GeneralData | null) =>
      dispatch(updateWorkshiftFilter(workshift, workshifts)),
    updateDateFilter: (dateStart, dateEnd, isManual) => dispatch(updateDateFilter(dateStart, dateEnd, isManual)),
  }
}

export type Props = WithTranslation & RouteComponentProps<any> & DispatchProps & StateProps

class PlantAnalysis extends React.Component<Props, OwnState> {
  // @ts-ignore
  private selectDateText: string
  // @ts-ignore
  private mounted: boolean

  constructor(props: Props) {
    super(props)

    this.selectDateText = this.props.t('plantAnalysis.actions.selectDateText')

    this.state = {
      startDate: moment(this.props.dateFilterStart),
      endDate: moment(this.props.dateFilterStart),
      currentGraph: ['M001'],
      lastScrollY: 0,
    }

    this.toggleCollapse = this.toggleCollapse.bind(this)
    this.handleScroll = this.handleScroll.bind(this)
  }

  public UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (
      (nextProps.dateFilterStart !== this.props.dateFilterStart || nextProps.workshift !== this.props.workshift) &&
      nextProps.workshift &&
      moment.isMoment(nextProps.dateFilterStart)
    ) {
      this.props.generalFetchData(nextProps.workshift)
    }

    if (nextProps.plant.plant !== this.props.plant.plant && !this.props.plant.isLoading) {
      this.props.modelFetchData(nextProps.plant.plant)
    }
  }

  public componentDidMount() {
    this.mounted = true
    window.addEventListener('scroll', this.handleScroll)
    /****/
    const url = new URL(window.location.href)
    const manualDate = url.searchParams.get('manualDate')
    // const manualWorkshift = url.searchParams.get('manualWorkshift')
    if (manualDate && moment(manualDate).isValid()) {
      this.props.updateDateFilter(moment(manualDate), moment(manualDate), true)
    }
    /****/

    if (this.props.dateFilterStart && this.props.workshift && this.props.plant) {
      this.props.generalFetchData(this.props.workshift)
    }

    if (this.props.plant && this.props.plant.plant && !this.props.plant.isLoading) {
      this.props.modelFetchData(this.props.plant.plant)
    }
  }

  public componentWillUnmount() {
    this.mounted = false
    window.removeEventListener('scroll', this.handleScroll)
  }

  public render() {
    const { workshift, model, t, plant, chartConfigurations } = this.props
    const { currentGraph } = this.state

    const nodes = model && !model.fetching && !model.error && model.data ? flatData(model.data.model.content) : []

    const timeStart = (workshift && workshift.start && moment(workshift.start).format('HH:mm')) || null
    const timeEnd =
      (workshift &&
        workshift.end &&
        workshift.start &&
        workshift.start !== workshift.end &&
        moment(workshift.end).format('HH:mm')) ||
      null
    const notEnded = workshift && !workshift.end
    const running =
      workshift &&
      workshift.end &&
      timeEnd &&
      moment(workshift.end).isSame(moment.now(), 'day') &&
      timeEnd === moment().format('HH:mm')

    const url = new URL(window.location.href)
    const allOpened = url.searchParams.get('allOpened')

    return (
      <div
        className={'mv4iot-fe-plant-analysis plant-analysis-list' + (this.state.lastScrollY > 48 ? 'is-scrolling' : '')}
      >
        <header>
          <div className={'flex-row justify-content-between'}>
            <Row horizontalAlignment={'between'} spacing={{ horizontal: false, vertical: false }}>
              <Column md={10}>
                <h1 className="title mb-3">{t('plantAnalysis.navigation.plantAnalysis')}</h1>
                <ul className="top-details">
                  <li>
                    <span className="label black">{t('plantAnalysis.labels.plantH')}</span> {workshift && workshift.h}
                  </li>
                  <li>
                    <span className="label">{t('plantAnalysis.labels.workshift.start')}</span> {timeStart}
                  </li>
                  <li>
                    <span className="label">{t('plantAnalysis.labels.workshift.end')}</span>
                    {running
                      ? t('plantAnalysis.labels.workshift.running')
                      : notEnded
                      ? t('plantAnalysis.labels.workshift.notEnded')
                      : timeEnd}
                  </li>
                </ul>
              </Column>
              <Column md={2}>
                <Row horizontalAlignment={'end'}>
                  <WorkshiftSelector isRenderVisible={true} />
                </Row>
              </Column>
            </Row>
          </div>
        </header>
        <div className="content">
          <div id="container">
            <React.Fragment>
              {model && !model.fetching && (!nodes || nodes.length === 0) ? (
                <Row horizontalAlignment={'center'}>
                  <Column sm={6}>
                    <div className="alert alert-danger w-100">{t('plantAnalysis.noNodes')}</div>
                  </Column>
                </Row>
              ) : !['ro', 'ric-sg'].includes(plant.plant) ? (
                // @ts-ignore
                <div className="graph-wrapper">
                  <GraphWrapper
                    active={currentGraph}
                    component={{ id: 'general', label: t('plantAnalysis.labels.general') }}
                    toggleCollapse={this.toggleCollapse}
                    subtitle={'graph2.subTitle'}
                  >
                    <GeneralGraphs
                      component={{ id: 'general', label: 'general' }}
                      active={currentGraph}
                      toggleCollapse={this.toggleCollapse}
                      forceFetch={true}
                    />
                  </GraphWrapper>
                </div>
              ) : (
                false
              )}
              {nodes
                // components
                .filter(c => !ASSETS_CODES_TO_BE_IGNORED.includes(c.code))
                .sort((a: ModelNodeData, b: ModelNodeData) => {
                  if (a.code < b.code) {
                    return -1
                  }
                  if (a.code > b.code) {
                    return 1
                  }
                  return 0
                })
                .map(c => {
                  const [baseNodeType, decoratorNodeType, ...rest] = c.nodeType.split('-')
                  const nodeAdditionalMeasures = rest ?? []

                  switch (baseNodeType) {
                    case 'PLANT_DATA_ROT_SCR':
                    case 'PLANT_DATA_BELT':
                    case 'PLANT_DATA_ROT_SCR_INVERTER':
                    case 'PLANT_DATA_BELT_INVERTER':
                      return (
                        <BeltChart currentGraph={currentGraph} component={c} toggleCollapse={this.toggleCollapse} />
                      )
                    case 'PLANT_DATA_BNK':
                    case 'PLANT_DATA_BNK_INVERTER':
                      return (
                        <BunkerChart currentGraph={currentGraph} component={c} toggleCollapse={this.toggleCollapse} />
                      )
                    case 'PLANT_DATA_ASP_INVERTER':
                      return (
                        <div className="graph-wrapper" key={c.id}>
                          <GraphWrapper
                            active={currentGraph}
                            component={c}
                            toggleCollapse={this.toggleCollapse}
                            subtitle={'graph4.subTitle'}
                          >
                            <FanGraphs t={true} component={c} inverter={true} />
                          </GraphWrapper>
                        </div>
                      )
                    case 'PLANT_DATA_ASP':
                      return (
                        <div className="graph-wrapper" key={c.id}>
                          <GraphWrapper
                            active={currentGraph}
                            component={c}
                            toggleCollapse={this.toggleCollapse}
                            subtitle={'graph4.subTitle'}
                          >
                            <FanGraphs t={true} component={c} />
                          </GraphWrapper>
                        </div>
                      )
                    case 'PLANT_DATA_BRT':
                      return (
                        <div className="graph-wrapper" key={c.id}>
                          <GraphWrapper active={currentGraph} component={c} toggleCollapse={this.toggleCollapse}>
                            <BagGraphs t={true} component={c} />
                          </GraphWrapper>
                        </div>
                      )
                    case 'PLANT_DATA_BELT_LOADER':
                    case 'PLANT_DATA_DEC':
                      return (
                        <div className="graph-wrapper" key={c.id}>
                          <GraphWrapper
                            active={currentGraph}
                            component={c}
                            toggleCollapse={this.toggleCollapse}
                            subtitle={'graph4.subTitle'}
                            zeroPadding={true}
                            manuallyOpened={!!(allOpened && allOpened === '1')}
                          >
                            <MultiSeriesLineCharts
                              component={c}
                              measures={
                                chartConfigurations &&
                                plant &&
                                chartConfigurations[plant.plant] &&
                                chartConfigurations[plant.plant][c.id]
                              }
                            />
                          </GraphWrapper>
                        </div>
                      )
                    /** BLUEAIR */
                    case 'SG2_PLANT_CHART': {
                      const getBinaryWords = (): ChartWordMeasure[] => {
                        if (isWordSlug(decoratorNodeType)) {
                          return [{ id: 'WORD01_INFO', type: decoratorNodeType }]
                        }
                        return []
                      }

                      const getMeasures = (): ChartMeasure[] => {
                        const measures: ChartMeasure[] = []
                        if (decoratorNodeType === 'INV') {
                          measures.push({
                            slug: 'amperage',
                            id: 'ACT_CUR',
                            unitOfMeasurement: 'A',
                            representation: 'line',
                          })
                        }

                        if (nodeAdditionalMeasures) {
                          if (nodeAdditionalMeasures.includes('CUR')) {
                            measures.push({
                              slug: 'amperage',
                              id: 'ACT_CUR',
                              unitOfMeasurement: 'A',
                              representation: 'line',
                            })
                          }
                          if (nodeAdditionalMeasures.includes('SPD')) {
                            measures.push({
                              slug: 'driveSpeed',
                              id: 'SPD_ACT',
                              unitOfMeasurement: '%',
                              representation: 'line',
                            })
                          }
                          if (nodeAdditionalMeasures.includes('PRS')) {
                            measures.push({
                              slug: 'pressure',
                              id: 'ACT_PRS',
                              unitOfMeasurement: 'bar',
                              representation: 'line',
                            })
                          }
                          if (nodeAdditionalMeasures.includes('MTMP')) {
                            measures.push({
                              slug: 'materialTemp',
                              id: 'MAT_ACT_TMP',
                              unitOfMeasurement: '°C',
                              representation: 'line',
                            })
                          }
                        }

                        return measures
                      }

                      const getPlugins = (): ChartPlugin[] => {
                        const plugins: ChartPlugin[] = []
                        // TODO: find better way to determine if the graph should show the speed absorption ratio summary
                        if (
                          [
                            'IBSGN5PHK0LGIKWX',
                            'IBSGN5PHK0LIVOYW',
                            'IBSGN5PHK0N1NGHY-1',
                            'IBSGN5PHK0N2BKVI',
                            'IBSGN5PHK0OCP5TT-1',
                            'IBSGN5PHK0ODD3AE',
                          ].includes(c.id)
                        ) {
                          plugins.push(speedAbsorptionRatioSummaryPlugin)
                        }
                        return plugins
                      }

                      return (
                        <ChartWrapper currentGraph={currentGraph} component={c} toggleCollapse={this.toggleCollapse}>
                          {({ plant: _plant, workShift: _workShift }) => (
                            <GenericBinarySrcGraph
                              id={c.id}
                              binaryWords={getBinaryWords()}
                              measures={getMeasures()}
                              plugins={getPlugins()}
                              plant={_plant}
                              workShift={_workShift}
                            />
                          )}
                        </ChartWrapper>
                      )
                    }
                    case 'SG2_PLANT_ROTATING_SCREEN': {
                      return (
                        <ChartWrapper currentGraph={currentGraph} component={c} toggleCollapse={this.toggleCollapse}>
                          {({ plant: _plant, workShift: _workShift }) => (
                            <GenericBinarySrcGraph
                              id={c.id}
                              binaryWords={[{ id: 'WORD01_INFO', type: WordSlug['ROT-SCREEN'] }]}
                              plant={_plant}
                              workShift={_workShift}
                              measures={[
                                {
                                  slug: 'amperage',
                                  id: 'ACT_CUR',
                                  unitOfMeasurement: 'A',
                                  representation: 'line',
                                },
                                {
                                  slug: 'driveSpeed',
                                  id: 'SPD_ACT',
                                  unitOfMeasurement: '%',
                                  representation: 'line',
                                },
                              ]}
                            />
                          )}
                        </ChartWrapper>
                      )
                    }
                    case 'SG2_PLANT_KEYS': {
                      return (
                        <ChartWrapper currentGraph={currentGraph} component={c} toggleCollapse={this.toggleCollapse}>
                          {({ plant: _plant, workShift: _workShift }) => (
                            <GenericBinarySrcGraph
                              id={c.id}
                              binaryWords={[{ id: 'WORD01_INFO', type: WordSlug.PKEY }]}
                              plant={_plant}
                              workShift={_workShift}
                            />
                          )}
                        </ChartWrapper>
                      )
                    }
                    case 'SG2_PLANT_DATA_DENSIFIER_BELT': {
                      return (
                        <ChartWrapper currentGraph={currentGraph} component={c} toggleCollapse={this.toggleCollapse}>
                          {({ plant: _plant, workShift: _workShift }) => (
                            <GenericBinarySrcGraph
                              id={c.id}
                              measures={[
                                {
                                  slug: 'amperage',
                                  id: 'CORRENTE_NASTRO',
                                  unitOfMeasurement: 'A',
                                  representation: 'line',
                                },
                                {
                                  assetId: loadingBeltIdToDensifierId[c.id as keyof typeof loadingBeltIdToDensifierId],
                                  slug: 'beltSpeed',
                                  id: 'SPEED_NASTRO',
                                  unitOfMeasurement: '%',
                                  representation: 'line',
                                },
                                {
                                  slug: 'densifierBeltState',
                                  id: 'NASTRO_CARICO',
                                  representation: 'time-bar',
                                },
                              ]}
                              plant={_plant}
                              workShift={_workShift}
                            />
                          )}
                        </ChartWrapper>
                      )
                    }
                    case 'SG2_PLANT_VALVE': {
                      return (
                        <ChartWrapper currentGraph={currentGraph} component={c} toggleCollapse={this.toggleCollapse}>
                          {({ plant: _plant, workShift: _workShift }) => (
                            <GenericBinarySrcGraph
                              id={c.id}
                              plant={_plant}
                              workShift={_workShift}
                              measures={[
                                {
                                  slug: 'waterFlow',
                                  id: 'H2O_ACT_FLW',
                                  unitOfMeasurement: 'l/h',
                                  representation: 'line',
                                },
                              ]}
                            />
                          )}
                        </ChartWrapper>
                      )
                    }
                    case 'SG2_PLANT_DATA_SCREW_CUR': {
                      return (
                        <ChartWrapper currentGraph={currentGraph} component={c} toggleCollapse={this.toggleCollapse}>
                          {({ plant: _plant, workShift: _workShift }) => (
                            <GenericBinarySrcGraph
                              id={c.id}
                              warnAreas={[
                                { slug: 'runningWarn', measure: 'running', fn: (value: number) => value === 0 },
                              ]}
                              binaryWords={[{ id: 'WORD01_INFO', type: WordSlug.MDIR }]}
                              measures={[
                                {
                                  slug: 'amperage',
                                  id: 'ACT_CUR',
                                  unitOfMeasurement: 'A',
                                  representation: 'line',
                                },
                              ]}
                              plant={_plant}
                              workShift={_workShift}
                            />
                          )}
                        </ChartWrapper>
                      )
                    }
                    case 'SG2_PLANT_DATA_SCREW_TEMP': {
                      return (
                        <ChartWrapper currentGraph={currentGraph} component={c} toggleCollapse={this.toggleCollapse}>
                          {({ plant: _plant, workShift: _workShift }) => (
                            <GenericBinarySrcGraph
                              id={c.id}
                              warnAreas={[
                                { slug: 'runningWarn', measure: 'running', fn: (value: number) => value === 0 },
                              ]}
                              binaryWords={[{ id: 'WORD01_INFO', type: WordSlug.MDIR }]}
                              measures={[
                                {
                                  slug: 'materialTemp',
                                  id: 'MAT_ACT_TMP',
                                  unitOfMeasurement: '°C',
                                  representation: 'line',
                                },
                              ]}
                              plant={_plant}
                              workShift={_workShift}
                            />
                          )}
                        </ChartWrapper>
                      )
                    }
                    case 'SG2_PLANT_DATA_SCREW_CUR_TEMP': {
                      return (
                        <ChartWrapper currentGraph={currentGraph} component={c} toggleCollapse={this.toggleCollapse}>
                          {({ plant: _plant, workShift: _workShift }) => (
                            <GenericBinarySrcGraph
                              id={c.id}
                              warnAreas={[
                                { slug: 'runningWarn', measure: 'running', fn: (value: number) => value === 0 },
                              ]}
                              binaryWords={[{ id: 'WORD01_INFO', type: WordSlug.MDIR }]}
                              measures={[
                                {
                                  slug: 'amperage',
                                  id: 'ACT_CUR',
                                  unitOfMeasurement: 'A',
                                  representation: 'line',
                                },
                                {
                                  slug: 'materialTemp',
                                  id: 'MAT_ACT_TMP',
                                  unitOfMeasurement: '°C',
                                  representation: 'line',
                                },
                              ]}
                              plant={_plant}
                              workShift={_workShift}
                            />
                          )}
                        </ChartWrapper>
                      )
                    }
                    case 'SG2_PLANT_TRITURATORE': {
                      return (
                        <ChartWrapper currentGraph={currentGraph} component={c} toggleCollapse={this.toggleCollapse}>
                          {({ plant: _plant, workShift: _workShift }) => (
                            <GenericBinarySrcGraph
                              id={c.id}
                              warnAreas={[
                                { slug: 'runningWarn', measure: 'running', fn: (value: number) => value === 0 },
                              ]}
                              binaryWords={[{ id: 'WORD01_INFO', type: WordSlug.MDIR }]}
                              measures={[
                                {
                                  slug: 'amperage',
                                  id: 'MAIN_DRIVERS_CURRENT',
                                  unitOfMeasurement: 'A',
                                  representation: 'line',
                                },
                                {
                                  slug: 'rotorSpeed',
                                  id: 'DRIVE1_SPEED_PERCENT',
                                  unitOfMeasurement: '%',
                                  representation: 'line',
                                },
                                {
                                  slug: 'pressorThrust',
                                  id: 'RAM_FEED_PERCENT',
                                  unitOfMeasurement: '%',
                                  representation: 'line',
                                },
                              ]}
                              plant={_plant}
                              workShift={_workShift}
                            />
                          )}
                        </ChartWrapper>
                      )
                    }
                    case 'SG2_PLANT_TRITURATORE_UNTHA': {
                      return (
                        <ChartWrapper currentGraph={currentGraph} component={c} toggleCollapse={this.toggleCollapse}>
                          {({ plant: _plant, workShift: _workShift }) => (
                            <GenericBinarySrcGraph
                              id={c.id}
                              warnAreas={[
                                { slug: 'runningWarn', measure: 'running', fn: (value: number) => value === 0 },
                              ]}
                              binaryWords={[{ id: 'WORD01_INFO', type: WordSlug.MDIR }]}
                              measures={[
                                {
                                  slug: 'amperage',
                                  id: 'MAIN_DRIVERS_CURRENT',
                                  unitOfMeasurement: 'A',
                                  representation: 'line',
                                },
                                {
                                  slug: 'rotorSpeed',
                                  id: 'ROT_ACT_SPD',
                                  unitOfMeasurement: '%',
                                  representation: 'line',
                                },
                                {
                                  slug: 'program',
                                  id: 'ACT_PROGRAM',
                                  representation: 'time-bar',
                                },
                              ]}
                              plant={_plant}
                              workShift={_workShift}
                            />
                          )}
                        </ChartWrapper>
                      )
                    }
                    case 'SG2_PLANT_BUFFER_SINGLE':
                      return (
                        <ChartWrapper currentGraph={currentGraph} component={c} toggleCollapse={this.toggleCollapse}>
                          {({ plant: _plant, workShift: _workShift }) => (
                            <GenericBinarySrcGraph
                              id={c.id}
                              binaryWords={[{ id: 'WORD01_INFO', type: WordSlug.SYNC }]}
                              plant={_plant}
                              workShift={_workShift}
                            />
                          )}
                        </ChartWrapper>
                      )
                    case 'SG2_PLANT_KH_VALVE':
                      return (
                        <ChartWrapper currentGraph={currentGraph} component={c} toggleCollapse={this.toggleCollapse}>
                          {({ plant: _plant, workShift: _workShift }) => (
                            <GenericBinarySrcGraph
                              id={c.id}
                              binaryWords={[{ id: 'WORD01_INFO', type: WordSlug.KH }]}
                              plant={_plant}
                              workShift={_workShift}
                            />
                          )}
                        </ChartWrapper>
                      )
                    case 'SG2_PLANT_DATA_BELT_INVERTER_MONO':
                    case 'SG2_PLANT_DATA_BELT_INVERTER':
                    case 'SG2_PLANT_DATA_BELT_MONO':
                    case 'SG2_PLANT_DATA_BELT':
                    case 'SG2_PLANT_DATA_MAT': {
                      const waterFlowIsPresent = ['SG2_PLANT_MULINO'].includes(baseNodeType)
                      const externalMaterialTemperatureIsPresent =
                        ['SG2_PLANT_DATA_MAT'].includes(baseNodeType) &&
                        (decoratorNodeType as string).toLowerCase() !== 'inv'
                      const additionlMaterialTemperatureIsPresent =
                        ['SG2_PLANT_DATA_MAT'].includes(baseNodeType) &&
                        (decoratorNodeType as string).toLowerCase() === 'inv'
                      return (
                        <div className="graph-wrapper" key={c.id}>
                          <GraphWrapper
                            active={currentGraph}
                            component={c}
                            toggleCollapse={this.toggleCollapse}
                            subtitle={baseNodeType.indexOf('INVERTER') >= 0 ? 'graph2.subTitle' : null}
                          >
                            <BeltBinarySrcGraphs
                              t={true}
                              binaryConfiguration={
                                this.props.detailsConfiguration?.[this.props.plant.plant ?? '']?.[
                                  decoratorNodeType ?? 'default'
                                ].binaryMapping
                              }
                              component={c}
                              measureActCurrentName={baseNodeType}
                              mono={[
                                'SG2_PLANT_DATA_BELT_INVERTER_MONO',
                                'SG2_PLANT_DATA_BELT_MONO',
                                'SG2_PLANT_DATA_MAT',
                              ].includes(baseNodeType)}
                              inverter={[
                                'SG2_PLANT_DATA_BELT_INVERTER_MONO',
                                'SG2_PLANT_DATA_BELT_INVERTER',
                                'SG2_PLANT_TRITURATORE',
                                'SG2_PLANT_MULINO',
                              ].includes(baseNodeType)}
                              externalChartLines={[
                                ...(externalMaterialTemperatureIsPresent ? [materialTemperatureAdditionalLine] : []),
                              ]}
                              additionalChartLines={[
                                ...(waterFlowIsPresent ? [waterFlowAdditionalLine] : []),
                                ...(additionlMaterialTemperatureIsPresent ? [materialTemperatureAdditionalLine] : []),
                              ]}
                              hideAvind={['SG2_PLANT_TRITURATORE', 'SG2_PLANT_MULINO'].includes(baseNodeType)}
                              hideSpeed={['SG2_PLANT_TRITURATORE', 'SG2_PLANT_MULINO'].includes(baseNodeType)}
                              subnodeType={decoratorNodeType}
                            />
                          </GraphWrapper>
                        </div>
                      )
                    }
                    case 'SG2_PLANT_MULINO': {
                      return (
                        <ChartWrapper currentGraph={currentGraph} component={c} toggleCollapse={this.toggleCollapse}>
                          {({ plant: _plant, workShift: _workShift }) => (
                            <GenericBinarySrcGraph
                              id={c.id}
                              warnAreas={[
                                { slug: 'runningWarn', measure: 'running', fn: (value: number) => value === 0 },
                              ]}
                              binaryWords={[{ id: 'WORD01_INFO', type: WordSlug.MDIR }]}
                              measures={[
                                {
                                  slug: 'amperage',
                                  id: 'ACT_CUR',
                                  unitOfMeasurement: 'A',
                                  representation: 'line',
                                },
                              ]}
                              plant={_plant}
                              workShift={_workShift}
                            />
                          )}
                        </ChartWrapper>
                      )
                    }
                    case 'SG2_PLANT_DEC':
                      return (
                        <div className="graph-wrapper" key={c.id}>
                          <GraphWrapper active={currentGraph} component={c} toggleCollapse={this.toggleCollapse}>
                            <BeltBinarySrcGenGraphs
                              binaryConfiguration={
                                this.props.detailsConfiguration?.[this.props.plant.plant ?? '']?.[
                                  decoratorNodeType ?? 'MDIR'
                                ].binaryMapping
                              }
                              component={c}
                              inverter={['SG2_PLANT_DEC'].includes(c.nodeType)}
                              measureActCurrentName={'_MT_DEC_ACT_CUR'}
                            />
                          </GraphWrapper>
                        </div>
                      )
                    case 'SG2_PLANT_BUFFER': {
                      return (
                        <ChartWrapper currentGraph={currentGraph} component={c} toggleCollapse={this.toggleCollapse}>
                          {({ plant: _plant, workShift: _workShift }) => (
                            <BuffersFillGraph id={c.id} plant={_plant} workShift={_workShift} />
                          )}
                        </ChartWrapper>
                      )
                    }
                    case 'PLANT_DATA_PRESS': {
                      return (
                        <div className="graph-wrapper" key={c.id}>
                          <GraphWrapper active={currentGraph} component={c} toggleCollapse={this.toggleCollapse}>
                            <PressDetail measureId={c.id} workShift={this.props.workshift} />
                          </GraphWrapper>
                        </div>
                      )
                    }
                    case 'PLANT_DATA_IMP_ASP': {
                      return (
                        <div className="graph-wrapper" key={c.id}>
                          <GraphWrapper active={currentGraph} component={c} toggleCollapse={this.toggleCollapse}>
                            <AspirationSystem measureId={c.id} workShift={this.props.workshift} />
                          </GraphWrapper>
                        </div>
                      )
                    }
                    default:
                      return null
                  }
                })}
            </React.Fragment>
          </div>
        </div>
      </div>
    )
  }

  private toggleCollapse(id: string) {
    this.setState({
      currentGraph: id && this.state.currentGraph.indexOf(id) > -1 ? [] : [id],
    })
  }

  private handleScroll() {
    const lastScrollY = window.scrollY

    if (!ticking) {
      window.requestAnimationFrame(() => {
        this.setState({
          lastScrollY,
        })
        ticking = false
      })

      ticking = true
    }
  }
}

export default withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(withTranslation()(PlantAnalysis)))
